import React, { useState } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { FloorPlan } from './FloorPlan';
import { ProductSearch } from './ProductSearch';
import { CartPanel } from './CartPanel';
import { PaymentDialog } from './PaymentDialog';
import { WaiterSelector } from './WaiterSelector';
import { TransferTableDialog } from './TransferTableDialog';
import { SplitBillDialog } from './SplitBillDialog';
import { usePosRestaurante } from '../hooks/usePosRestaurante';
import { usePosStore } from '../store/posStore';
import type { MesaResponse } from '../types/pos.types';
import { ESTADOS_MESA, TIPOS_ORDEN } from '../types/pos.types';

export const PosRestaurantePage: React.FC = () => {
  const {
    pisos,
    pisoActivo,
    setPisoActivo,
    mesaSeleccionada,
    setMesaSeleccionada,
    loading,
    loadPisos,
    cambiarEstadoMesa,
    asignarMozo,
    transferirMesa,
    separarCuenta,
  } = usePosRestaurante();

  const { crearOrden, seleccionarOrden, ordenActual } = usePosStore();

  const [waiterDialogOpen, setWaiterDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);

  const handleMesaClick = async (mesa: MesaResponse) => {
    setMesaSeleccionada(mesa);

    if (mesa.idOrdenActiva) {
      await seleccionarOrden(mesa.idOrdenActiva);
    } else if (mesa.estado === ESTADOS_MESA.DISPONIBLE) {
      await cambiarEstadoMesa(mesa.idMesa, ESTADOS_MESA.OCUPADA);
      await crearOrden({
        tipoOrden: TIPOS_ORDEN.RESTAURANTE,
        idMesa: mesa.idMesa,
      });
    }
  };

  const handleBackToFloor = () => {
    setMesaSeleccionada(null);
    loadPisos();
  };

  const handleTransfer = async (idMesaDestino: number) => {
    if (!mesaSeleccionada) return;
    await transferirMesa(mesaSeleccionada.idMesa, idMesaDestino);
    setTransferDialogOpen(false);
    handleBackToFloor();
  };

  const handleSplit = async (itemIds: number[]) => {
    if (!mesaSeleccionada) return;
    await separarCuenta(mesaSeleccionada.idMesa, itemIds);
    setSplitDialogOpen(false);
    loadPisos();
  };

  const handleAssignWaiter = async (idMozo: number) => {
    if (!mesaSeleccionada) return;
    await asignarMozo(mesaSeleccionada.idMesa, idMozo);
    setWaiterDialogOpen(false);
    loadPisos();
  };

  // Mesa selected: show product search + cart
  if (mesaSeleccionada) {
    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Mesa header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <IconButton size="small" onClick={handleBackToFloor}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight="bold">
              Mesa {mesaSeleccionada.numero}
            </Typography>
            {mesaSeleccionada.nombre && (
              <Typography variant="body2" color="text.secondary">
                - {mesaSeleccionada.nombre}
              </Typography>
            )}
            {mesaSeleccionada.mozoNombre && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                Mozo: {mesaSeleccionada.mozoNombre}
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" variant="outlined" onClick={() => setWaiterDialogOpen(true)}>
              Asignar Mozo
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setTransferDialogOpen(true)}
              disabled={!ordenActual}
            >
              Transferir
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSplitDialogOpen(true)}
              disabled={!ordenActual || (ordenActual.detalles?.length ?? 0) < 2}
            >
              Separar Cuenta
            </Button>
          </Box>

          {/* Content: Products + Cart */}
          <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
            <Box
              sx={{
                flex: '1 1 65%',
                display: 'flex',
                flexDirection: 'column',
                p: 1,
                overflow: 'hidden',
              }}
            >
              <ProductSearch />
            </Box>
            <Box
              sx={{
                flex: '1 1 35%',
                borderLeft: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <CartPanel />
            </Box>
          </Box>
        </Box>

        <PaymentDialog />
        <WaiterSelector
          open={waiterDialogOpen}
          onClose={() => setWaiterDialogOpen(false)}
          onSelect={handleAssignWaiter}
        />
        <TransferTableDialog
          open={transferDialogOpen}
          onClose={() => setTransferDialogOpen(false)}
          onTransfer={handleTransfer}
          pisos={pisos}
          mesaActualId={mesaSeleccionada.idMesa}
        />
        <SplitBillDialog
          open={splitDialogOpen}
          onClose={() => setSplitDialogOpen(false)}
          onSplit={handleSplit}
          items={ordenActual?.detalles ?? []}
        />
      </>
    );
  }

  // Default: Floor plan
  return (
    <FloorPlan
      pisos={pisos}
      pisoActivo={pisoActivo}
      onPisoChange={setPisoActivo}
      onMesaClick={handleMesaClick}
      loading={loading}
    />
  );
};

export default PosRestaurantePage;
